import type { components } from "./generated/trading-engine.js";
import { requireData } from "./api-error.js";
import type { TradingEngineClient } from "./client.js";

type Operation = components["schemas"]["Operation"];

export interface PollOperationOptions {
  readonly intervalMilliseconds?: number;
  readonly signal?: AbortSignal;
  readonly timeoutMilliseconds?: number;
}

export async function pollOperation(
  client: TradingEngineClient,
  operationId: string,
  options: PollOperationOptions = {},
): Promise<Operation> {
  const started = Date.now();
  const timeout = options.timeoutMilliseconds ?? 120_000;
  while (Date.now() - started <= timeout) {
    options.signal?.throwIfAborted();
    const result = await client.GET("/operations/{operationId}", {
      params: { path: { operationId } },
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
    const operation = requireData(result);
    if (operation.status !== "running") return operation;
    await new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(resolve, options.intervalMilliseconds ?? 1_500);
      options.signal?.addEventListener(
        "abort",
        () => {
          window.clearTimeout(timer);
          reject(new DOMException("Operation polling was aborted.", "AbortError"));
        },
        { once: true },
      );
    });
  }
  throw new Error("TradingEngine operation polling timed out.");
}
